import React, { useState, useMemo, Fragment, useCallback, useRef } from 'react';
import { Column } from '@harmony/enablers/components/data-grid/data-grid';
import { FlyInPanel, Button, Combobox, Option, Icon } from '../providers/harmony-scope/lazy-enablers';
import { useTrackInteraction } from '../services/logging/hooks';
import { usePageContext } from '../utils/page-context';
import { useMEMTranslation } from '../utils/text-utils';

export type TTableFilters = { [key: string]: { [key: string]: boolean } };

export type TColumnData = Column & {
  filterable?: boolean;
};

interface Iprops {
  header?: string;
  open: boolean;
  columns: TColumnData[];
  rows: any[];
  tableFilters: TTableFilters;
  handleApplyFilters: (filters: TTableFilters) => void;
  onHeAfterHide: (event: CustomEvent<unknown>) => void;
}

const componentName: string = 'Table Filter Panel';

export const TableFiltersPanel = (props: Iprops) => {
  const { header, columns, rows, open, tableFilters: initTableFilters, onHeAfterHide = () => {} } = props;
  const pageName = usePageContext();
  const trackInteraction = useTrackInteraction();
  const { t } = useMEMTranslation();
  const trackSpecificInteraction = useCallback(
    (action: string) => {
      trackInteraction(`${action} Table Filter`, pageName, componentName);
    },
    [pageName, trackInteraction]
  );

  const [tableFilters, setTableFilters] = useState<TTableFilters>(initTableFilters);
  const ariaLiveRegionRef = useRef<HTMLDivElement | null>(null);
  const clearButtonRef = useRef<HTMLButtonElement | null>(null);

  const filterableColumns: TColumnData[] = useMemo(() => columns.filter((column) => column.filterable), [columns]);

  const filterOptionsByColumn: { [key: string]: { [key: string]: boolean } } = useMemo(() => {
    const filterOptions: { [key: string]: { [key: string]: boolean } } = {};
    filterableColumns.forEach((filterableColumn) => {
      if (!filterOptions[filterableColumn.field]) {
        filterOptions[filterableColumn.field] = {};
      }
      rows.forEach((row) => {
        if (row.cells) {
          filterOptions[`${filterableColumn.field}`][`${row.cells[`${filterableColumn.field}`]}`] = true;
        } else {
          filterOptions[`${filterableColumn.field}`][`${row[`${filterableColumn.field}`]}`] = true;
        }
      });
    });
    return filterOptions;
  }, [filterableColumns, rows]);

  const disabledColumns = useMemo(() => {
    const disabled: { [key: string]: boolean } = {};
    filterableColumns.forEach(({ field }) => {
      if (Object.keys(filterOptionsByColumn[field]).length === 1) {
        disabled[field] = true;
      }
    });
    return disabled;
  }, [filterableColumns, filterOptionsByColumn]);

  const handleFilterChange = useCallback(
    (filterType: string) => (event: any) => {
      const valuesAsArray = event?.target?._valueAsArray;
      if (!valuesAsArray) {
        return;
      }

      const newFilters = { ...tableFilters };
      newFilters[filterType] = {};
      valuesAsArray.forEach((filterValue: string) => {
        newFilters[filterType][filterValue] = true;
      });

      setTableFilters(newFilters);
      trackSpecificInteraction('Select');
    },
    [tableFilters, trackSpecificInteraction]
  );

  const clearFilters = useCallback(() => {
    setTableFilters({});
    trackSpecificInteraction('Clear');
    // Announce ARIA message for filters reseted successfully
    if (ariaLiveRegionRef.current) {
      ariaLiveRegionRef.current.textContent = t('ariaFilterClearSuccess');
      setTimeout(() => {
        if (ariaLiveRegionRef.current) {
          ariaLiveRegionRef.current.textContent = '';
        }
      }, 3000);
    }
    // Keep focus on clear button
    if (clearButtonRef.current) {
      clearButtonRef.current.focus();
    }
  }, [trackSpecificInteraction, t]);

  const applyFilters = useCallback(() => {
    props.handleApplyFilters(tableFilters);
    trackSpecificInteraction('Apply');
  }, [props, tableFilters, trackSpecificInteraction]);

  const filterValuesForColumn = useCallback((column: string = '') => Object.keys(tableFilters[column] || {})?.join(','), [tableFilters]);

  const handleRequestClose = (event: any) => {
    if (event?.detail?.source === 'keyboard') {
      event.preventDefault();
      return;
    }
  };

  return (
    <FlyInPanel onHeFlyInRequestClose={handleRequestClose} heading={header || t('tableFiltersPanelHeader')} open={open} onHeAfterHide={onHeAfterHide}>
      {filterableColumns.map(({ content, field }) => (
        <Fragment key={`select-${field}`}>
          <Combobox
            label={content}
            mode="tag-picker"
            autocomplete="both"
            freeform
            value={disabledColumns[field] ? Object.keys(filterOptionsByColumn[field])[0] : filterValuesForColumn(field)}
            onHeChange={handleFilterChange(field)}
            disabled={disabledColumns[field] || false}
          >
            {Object.keys(filterOptionsByColumn[field]).map((filterOption) => (
              <Option key={`${field}-${filterOption}`}>{filterOption}</Option>
            ))}
          </Combobox>
          <br />
        </Fragment>
      ))}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button appearance="secondary" onClick={clearFilters}>
          <>
            <Icon slot="start" name="clearfilter" />
            {t('tableFiltersPanelClear')}
          </>
        </Button>
        <Button appearance="primary" onClick={applyFilters}>
          {t('tableFiltersPanelApply')}
        </Button>
      </div>
      {/* ARIA live region for filter clear message */}
      <div
        ref={ariaLiveRegionRef}
        aria-live="assertive"
        aria-atomic="true"
        style={{ position: 'absolute', left: -9999, height: 1, width: 1, overflow: 'hidden' }}
      />
    </FlyInPanel>
  );
};
